import * as Sentry from '@sentry/react';

import rollbarLogger from 'config/rollbar';
import { AssetTypeCode } from 'types/models/asset-type';
import Sample from 'types/models/sample';
import SamplePoint, { SOIL_MOISTURE_SENSOR_TAG_IDS, SOIL_TEMPERATURE_SENSOR_TAG_IDS, SamplePointId } from 'types/models/samplePoint';
import convertMetricToImperial from 'utils/convert-metric-to-imperial';
import { UnitType } from 'utils/get-unit-by-country';
import { truncateWindSpeedPrecisionInSample } from 'utils/Sample/get-wind-measurements';
import { weatherAssetTypeToUnitType } from 'utils/SamplePoints/weather-station';

export function getUnitTypeByAssetTypeId(assetTypeId: number): UnitType {
  switch (assetTypeId) {
    case AssetTypeCode.PIPE:
      return UnitType.VOLUME_SM;
    case AssetTypeCode.RAIN_GAUGE:
      return UnitType.RAIN;
    case AssetTypeCode.LINE_PRESSURE:
      return UnitType.PRESSURE_LG;
    case AssetTypeCode.BATTERY:
      return UnitType.TREND_SM;
    case AssetTypeCode.BORE:
      return UnitType.LEVEL_MD;
    case AssetTypeCode.WATER_TANK:
    case AssetTypeCode.FUEL_TANK:
    case AssetTypeCode.LIQUID_FERTILISER_TANK:
    case AssetTypeCode.DAM:
    case AssetTypeCode.TROUGH:
    default:
      return weatherAssetTypeToUnitType({ assetType: assetTypeId }) ?? UnitType.LEVEL_SM;
  }
}

export function parseSample(
  sample: Sample,
  samplePointId: SamplePointId,
  assetTypeId: AssetTypeCode,
  country: string
) {
  if ([
    AssetTypeCode.BORE,
    AssetTypeCode.DAM,
    AssetTypeCode.PIPE,
    AssetTypeCode.RAIN_GAUGE,
    AssetTypeCode.EVAPOTRANSPIRATION,
    // Power-related units (V, A, kWh) do not need conversion between metric and imperial
    AssetTypeCode.POWER_METER,
    // Temperature and Dew Point are not handled in the convertMetricToImperial function below; that is good because
    // we leave the conversion to the UI layer
    AssetTypeCode.TEMPERATURE,
    AssetTypeCode.DEW_POINT
  ].includes(assetTypeId)) {
    return {
      ...sample,
      samplePointId: samplePointId
    };
  }

  // For wind samples, we need to perform localised rounding at the sample level for its multi-dimensional values.
  if (assetTypeId === AssetTypeCode.WIND) {
    return {
      ...truncateWindSpeedPrecisionInSample(sample, country),
      samplePointId: samplePointId
    };
  }

  // TODO: Need to gradually move unit conversion closer to UI layer, because
  // some calculation later needs metric values. Bore is the first step.
  // @uom-refactor
  const rwValue: number = convertMetricToImperial(
    getUnitTypeByAssetTypeId(assetTypeId),
    country,
    sample.rwValue
  ) || 0;

  return {
    ...sample,
    samplePointId: samplePointId,
    rwValue
  };
};

interface IParseSamples {
  samples: Sample[];
  samplePoint: SamplePoint;
  country: string;
}

export function parseSamples({
  samples, samplePoint, country
}: IParseSamples): Sample[] {
  return samples.map((sample) => parseSample(sample, samplePoint.id, samplePoint.assetTypeId, country));
}

export function getSoilMoistureAndTemperatureSamples(
  samplePoint: SamplePoint,
  samplesFromAssociatedSamplePoints: Sample[][]
): {
  soilMoistureSamples: Sample[];
  soilTemperatureSamples: Sample[];
} {
  let soilMoistureSamples: Sample[] = [];
  let soilTemperatureSamples: Sample[] = [];

  // Determine which is Moisture and Temperature from samplesFromAssociatedSamplePoints
  // If there are 2, then first is Moisture and second is Temperature
  // If there is 1, then it's either Moisture or Temperature. We use the sample point's sensorTags.id to
  // determine if it's Moisture or Temperature. We cannot use assetTypeId because it's already overridden
  // by the Soil sensor merge logic.
  if (samplesFromAssociatedSamplePoints.length === 2) {
    soilMoistureSamples = samplesFromAssociatedSamplePoints[0];
    soilTemperatureSamples = samplesFromAssociatedSamplePoints[1];
  } else if (samplesFromAssociatedSamplePoints.length === 1) {
    if (SOIL_MOISTURE_SENSOR_TAG_IDS.includes(samplePoint.sensorTags.id)) {
      soilMoistureSamples = samplesFromAssociatedSamplePoints[0];
    } else if (SOIL_TEMPERATURE_SENSOR_TAG_IDS.includes(samplePoint.sensorTags.id)) {
      soilTemperatureSamples = samplesFromAssociatedSamplePoints[0];
    } else {
      rollbarLogger.error('Unknown soil Sensor Tag ID', { samplePoint, sensorTagId: samplePoint.sensorTags.id });
      Sentry.logger.error('Unknown soil Sensor Tag ID', { samplePoint, sensorTagId: samplePoint.sensorTags.id });
    }
  }

  return { soilMoistureSamples, soilTemperatureSamples };
}
